import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';

// ── Types ────────────────────────────────────────────────────────────
interface FeedbackItem {
  id: string;
  user_id: string | null;
  submitter_name: string | null;
  category: string;
  message: string;
  screenshot_url: string | null;
  has_screenshot?: boolean;
  screenshot_status?: 'none' | 'protected' | 'legacy_unverified';
  app_version: string | null;
  device_model: string | null;
  os_name: string | null;
  os_version: string | null;
  status: string;
  admin_notes: string | null;
  handled_by_admin_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  resolved_at: string | null;
}

interface FeedbackResponse {
  feedback: FeedbackItem[];
  total: number;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const LIMIT = 50;

const CATEGORY_CONFIG: Record<string, { icon: string; label: string }> = {
  report_problem: { icon: '🐛', label: 'Report a Problem' },
  suggest_idea: { icon: '💡', label: 'Suggest an Idea' },
  improve_something: { icon: '✨', label: 'Improve Something' },
  dont_like: { icon: '😟', label: "Something I Don't Like" },
  general_feedback: { icon: '💗', label: 'General Feedback' },
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-amber-50 text-amber-700 border border-amber-200' },
  reviewing: { label: 'Reviewing', className: 'bg-blue-50 text-blue-700 border border-blue-200' },
  planned: { label: 'Planned', className: 'bg-purple-50 text-purple-700 border border-purple-200' },
  in_progress: { label: 'In Progress', className: 'bg-indigo-50 text-indigo-700 border border-indigo-200' },
  resolved: { label: 'Resolved', className: 'bg-green-50 text-green-700 border border-green-200' },
  rejected: { label: 'Rejected', className: 'bg-gray-100 text-gray-500 border border-gray-200' },
  duplicate: { label: 'Duplicate', className: 'bg-gray-100 text-gray-500 border border-gray-200' },
};

const STATUS_FILTER_OPTIONS = ['all', ...Object.keys(STATUS_CONFIG)];

// ── Toast Component (matches Users.tsx pattern) ────────────────────────
const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`
          flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border
          toast-enter cursor-pointer min-w-[280px]
          ${toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
          }
        `}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
      </div>
    ))}
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.className}`}>
      {cfg.label}
    </span>
  );
};

const SkeletonRow: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 5 }).map((_, i) => (
      <td key={i} className="px-4 py-4">
        <div className="h-4 bg-gray-200 rounded-lg w-3/4" />
      </td>
    ))}
  </tr>
);

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// ── Detail Modal ─────────────────────────────────────────────────────
const DetailModal: React.FC<{
  item: FeedbackItem;
  onClose: () => void;
  onSaved: (updated: FeedbackItem) => void;
  addToast: (msg: string, type: 'success' | 'error') => void;
}> = ({ item, onClose, onSaved, addToast }) => {
  const [status, setStatus] = useState(item.status);
  const [notes, setNotes] = useState(item.admin_notes || '');
  const [saving, setSaving] = useState(false);
  const [screenshot, setScreenshot] = useState<{ id: string; url: string } | null>(null);
  const [screenshotError, setScreenshotError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | undefined;
    const controller = new AbortController();
    setScreenshot(null);
    setScreenshotError(false);
    if (item.screenshot_status === 'protected') {
      adminApi.get<Blob>(`/admin/feedback/${encodeURIComponent(item.id)}/screenshot`, {
        responseType: 'blob', signal: controller.signal, timeout: 35_000,
      }).then(({ data }) => {
        if (cancelled) return;
        if (!(data instanceof Blob) || !['image/jpeg', 'image/png', 'image/webp'].includes(data.type) || data.size > 3_000_000) {
          setScreenshotError(true);
          return;
        }
        objectUrl = URL.createObjectURL(data);
        setScreenshot({ id: item.id, url: objectUrl });
      }).catch(() => { if (!cancelled) setScreenshotError(true); });
    }
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id, item.screenshot_status]);
  const cfg = CATEGORY_CONFIG[item.category] || { icon: '💬', label: item.category };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminApi.patch<FeedbackItem>(`/admin/feedback/${item.id}`, {
        status,
        admin_notes: notes,
      });
      addToast('Feedback updated.', 'success');
      onSaved(res.data);
    } catch (err: any) {
      addToast(err.response?.data?.detail || 'Failed to save changes.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between sticky top-0 bg-white rounded-t-2xl">
          <div>
            <span className="text-2xl mr-2">{cfg.icon}</span>
            <span className="text-lg font-bold text-gray-800">{cfg.label}</span>
            <p className="text-xs text-gray-400 mt-1">
              {item.submitter_name || 'Deleted user'} · {formatDate(item.created_at)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Message */}
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Message</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-gray-50 rounded-xl p-4">
              {item.message}
            </p>
          </div>

          {/* Screenshot */}
          {(item.has_screenshot || item.screenshot_url) && (
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Screenshot</h4>
              {screenshot?.id === item.id ? <img
                src={screenshot.url}
                alt="Feedback screenshot"
                className="max-h-72 rounded-xl border border-gray-200 object-contain"
              /> : <p className="text-sm text-gray-500">{
                item.screenshot_status !== 'protected'
                  ? 'This older screenshot requires a privacy review before viewing.'
                  : screenshotError ? 'Screenshot unavailable. Close and reopen to retry.' : 'Loading screenshot…'
              }</p>}
            </div>
          )}

          {/* Device info */}
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Device Info</h4>
            <div className="flex flex-wrap gap-2 text-xs text-gray-600">
              <span className="bg-gray-100 rounded-full px-3 py-1">App v{item.app_version || '—'}</span>
              <span className="bg-gray-100 rounded-full px-3 py-1">{item.device_model || 'Unknown device'}</span>
              <span className="bg-gray-100 rounded-full px-3 py-1">{item.os_name || '—'} {item.os_version || ''}</span>
            </div>
          </div>

          {/* Status */}
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Status</h4>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm
                focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {Object.keys(STATUS_CONFIG).map((s) => (
                <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
              ))}
            </select>
          </div>

          {/* Admin notes */}
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Admin Notes</h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Internal notes — not visible to the user"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm resize-none
                focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90
              disabled:opacity-50 transition-colors active:scale-95"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Feedback Component ──────────────────────────────────────────
const Feedback: React.FC = () => {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const toastIdRef = useRef(0);

  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const fetchFeedback = useCallback(async (currentOffset: number, status: string, category: string) => {
    setLoading(true);
    try {
      const params: Record<string, any> = { limit: LIMIT, offset: currentOffset };
      if (status !== 'all') params.status = status;
      if (category !== 'all') params.category = category;
      const res = await adminApi.get<FeedbackResponse>('/admin/feedback', { params });
      setItems(res.data.feedback);
      setTotal(res.data.total);
    } catch (err: any) {
      addToast('Failed to load feedback.', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchFeedback(offset, statusFilter, categoryFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, statusFilter, categoryFilter]);

  const handleFilterChange = (status: string) => {
    setStatusFilter(status);
    setOffset(0);
  };

  const handleCategoryChange = (category: string) => {
    setCategoryFilter(category);
    setOffset(0);
  };

  const handleSaved = (updated: FeedbackItem) => {
    setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
    setSelected(null);
  };

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">💬 Feedback & Ideas</h1>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span className="font-semibold text-gray-800">{total.toLocaleString()}</span>
          <span>total submissions</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTER_OPTIONS.map((s) => (
          <button
            key={s}
            onClick={() => handleFilterChange(s)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              statusFilter === s
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
          </button>
        ))}

        <select
          value={categoryFilter}
          onChange={(e) => handleCategoryChange(e.target.value)}
          className="ml-2 border border-gray-200 rounded-full px-3.5 py-1.5 text-xs font-semibold text-gray-600
            focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {['Category', 'Message', 'From', 'Submitted', 'Status'].map((col) => (
                  <th key={col} className="px-4 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <span className="text-4xl">💬</span>
                      <p className="text-gray-400 font-medium">No feedback found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const cfg = CATEGORY_CONFIG[item.category] || { icon: '💬', label: item.category };
                  return (
                    <tr
                      key={item.id}
                      onClick={() => setSelected(item)}
                      className="hover:bg-gray-50/50 transition-colors duration-150 cursor-pointer"
                    >
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span className="text-sm">{cfg.icon} {cfg.label}</span>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 max-w-[320px] truncate">
                        {item.message}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600">
                        {item.submitter_name || 'Deleted user'}
                      </td>
                      <td className="px-4 py-3.5 text-xs text-gray-400 whitespace-nowrap">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-4 py-3.5">
                        <StatusBadge status={item.status} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
            <span className="text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-700">{offset + 1}</span>
              –<span className="font-semibold text-gray-700">{Math.min(offset + LIMIT, total)}</span>
              {' '}of <span className="font-semibold text-gray-700">{total}</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
                disabled={offset === 0}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600
                  hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
              >
                Prev
              </button>
              <div className="flex items-center gap-1 px-3">
                <span className="text-sm font-bold text-primary">{currentPage}</span>
                <span className="text-sm text-gray-400">/</span>
                <span className="text-sm text-gray-500">{totalPages}</span>
              </div>
              <button
                onClick={() => setOffset((o) => (o + LIMIT < total ? o + LIMIT : o))}
                disabled={offset + LIMIT >= total}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600
                  hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <DetailModal
          item={selected}
          onClose={() => setSelected(null)}
          onSaved={handleSaved}
          addToast={addToast}
        />
      )}

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default Feedback;
